import type { ImageScenario } from '../game/imageTypes';
import { toolById } from '../game/partners';
import { SOLVED_AT } from '../game/progress';
import { play } from '../game/sound';

interface Props {
  scenario: ImageScenario;
  /** 1-based position in the campaign. Printed on the folder tab. */
  number: number;
  /** Best score so far on this level, if it has been played. */
  best?: number | undefined;
  onOpen: () => void;
}

/**
 * A level, presented as a case file.
 *
 * The thumbnail is the level's own screenshot rather than an illustration, for
 * two reasons: it is honest about what the player is walking into, and a menu
 * of five prose blocks that all describe "a Jira screen with arrows on it"
 * gives someone nothing to choose between. The picture does the choosing.
 */
export function CaseCard({ scenario, number, best, onOpen }: Props) {
  const closed = best !== undefined && best >= SOLVED_AT;

  return (
    <button
      type="button"
      className="case-card"
      onClick={() => {
        play('caseOpen');
        onOpen();
      }}
    >
      <span className="case-card__tab">Case {String(number).padStart(2, '0')}</span>

      <span className="case-card__photo">
        {/* Decorative: the title and docket line beside it already name the
            level, so an alt text here would just be read out twice. */}
        <img src={scenario.image.src} alt="" loading="lazy" />
      </span>

      <span className="case-card__body">
        <span className="case-card__title">{scenario.title}</span>
        <span className="case-card__meta">
          {toolById(scenario.source.toolId).short} · {scenario.arrows.length} arrows
        </span>
        <span className="case-card__line">{scenario.subtitle}</span>
      </span>

      {best !== undefined && (
        <span className="case-card__stamp">
          {closed ? 'Closed' : 'Best'} {best}
        </span>
      )}
    </button>
  );
}
